import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, TextField, Button, CircularProgress,
  Alert, Select, MenuItem, FormControl, InputLabel, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ScienceIcon from '@mui/icons-material/Science';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';

// ── helpers ────────────────────────────────────────────────────────────────────
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
function buildYears() {
  const y = new Date().getFullYear();
  return [y - 2, y - 1, y, y + 1];
}
const num = (v: string | number | null | undefined) =>
  v == null || v === '' || isNaN(Number(v)) ? null : Number(v);
const fmt = (v: number | null | undefined, dec = 2) =>
  v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const ratio = (fos: number | null, tac: number | null) =>
  fos != null && tac != null && tac !== 0 ? fos / tac : null;
const fmtDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
};

// ── column group config ───────────────────────────────────────────────────────
interface FieldDef { key: string; label: string; computed?: true }
interface GroupDef { label: string; bg: string; shared?: true; fields: FieldDef[] }

const fosGroup = (prefix: string): FieldDef[] => [
  { key: `${prefix}Fos`, label: 'FOS' },
  { key: `${prefix}Tac`, label: 'TAC' },
  { key: `${prefix}FosTac`, label: 'FOS/TAC', computed: true },
  { key: `${prefix}Ph`,  label: 'PH' },
  { key: `${prefix}Ds`,  label: 'DS' },
];

const GROUPS: GroupDef[] = [
  { label: 'F1',                   bg: '#c8e6c9', fields: fosGroup('f1')   },
  { label: 'F2',                   bg: '#bbdefb', fields: fosGroup('f2')   },
  { label: 'F3',                   bg: '#fff9c4', fields: fosGroup('f3')   },
  { label: 'IST',                  bg: '#ffccbc', fields: fosGroup('ist')  },
  { label: 'MP',                   bg: '#e0e0e0', shared: true, fields: [{ key: 'mpDs', label: 'DS' }] },
  { label: 'Gas Quality Ferm. 1',  bg: '#b3e5fc', shared: true, fields: fosGroup('gqf1') },
  { label: 'Gas Quality Ferm. 2',  bg: '#ffe0b2', shared: true, fields: fosGroup('gqf2') },
  { label: 'Gas Quality Ferm. 3',  bg: '#e8d5f5', shared: true, fields: fosGroup('gqf3') },
  { label: 'Gas Quality',          bg: '#f5f5f5', shared: true, fields: [
    { key: 'gqCo2', label: 'CO2' }, { key: 'gqO2', label: 'O2' },
    { key: 'gqH2s', label: 'H2S' }, { key: 'gqCh4', label: 'CH4' },
  ]},
];

// flat list of all editable fields (non-computed)
const FIELDS = GROUPS.flatMap(g => g.fields.filter(f => !f.computed).map(f => f.key));

// computed field helper
const computeField = (key: string, row: Record<string,string>): number | null => {
  if (!key.endsWith('FosTac')) return null;
  const base = key.replace('FosTac', '');
  return ratio(num(row[`${base}Fos`]), num(row[`${base}Tac`]));
};

// ── styles ────────────────────────────────────────────────────────────────────
const TH = (bg = '#1b5e20') => ({
  background: bg, color: bg === '#1b5e20' ? '#fff' : '#000',
  fontWeight: 700, fontSize: '0.62rem', textAlign: 'center' as const,
  px: '3px', py: '5px', borderRight: '1px solid rgba(0,0,0,0.1)', whiteSpace: 'nowrap' as const,
  lineHeight: 1.1,
});
const TD: React.CSSProperties = {
  padding: '2px 3px', fontSize: '0.68rem', textAlign: 'center', whiteSpace: 'nowrap',
  borderRight: '1px solid rgba(0,0,0,0.06)',
};
const TD_COMP: React.CSSProperties = {
  ...TD, fontWeight: 700, background: '#f1f8e9', color: '#2e7d32',
};
const TD_DS: React.CSSProperties = { ...TD, background: '#fff8e1' };
const TD_NS: React.CSSProperties = { ...TD, background: '#e8eaf6' };

type EditMap = Record<string, Record<string, string>>; // shift → field → value

// ── main component ────────────────────────────────────────────────────────────
const FostacMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const [tab, setTab] = useState(0);

  // ── Report state ──────────────────────────────────────────────────────────
  const [repMonth, setRepMonth]   = useState(new Date().getMonth() + 1);
  const [repYear,  setRepYear]    = useState(new Date().getFullYear());
  const [repRows,  setRepRows]    = useState<any[]>([]);
  const [repLoading, setRepLoading] = useState(false);
  const [repError,   setRepError]   = useState('');

  // ── Entry state ───────────────────────────────────────────────────────────
  const [entDate,    setEntDate]    = useState(today);
  const [edits,      setEdits]      = useState<EditMap>({ DS: {}, NS: {} });
  const [entLoading, setEntLoading] = useState(false);
  const [entSaving,  setEntSaving]  = useState(false);
  const [entError,   setEntError]   = useState('');
  const [entSuccess, setEntSuccess] = useState('');

  // ── Report fetch ──────────────────────────────────────────────────────────
  const loadReport = useCallback(async (month: number, year: number) => {
    setRepLoading(true); setRepError('');
    try {
      const { data } = await axios.get(`${API}/fostac/monthly-report`, {
        params: { month: `${year}-${String(month).padStart(2, '0')}` },
      });
      setRepRows(data);
    } catch { setRepError('Failed to load report.'); }
    finally { setRepLoading(false); }
  }, []);

  useEffect(() => { if (tab === 0) loadReport(repMonth, repYear); }, [tab, repMonth, repYear, loadReport]);

  // ── Entry fetch ───────────────────────────────────────────────────────────
  const loadEntry = useCallback(async (date: string) => {
    setEntLoading(true); setEntError(''); setEntSuccess('');
    setEdits({ DS: {}, NS: {} });
    try {
      const { data } = await axios.get(`${API}/fostac/date-report`, { params: { date } });
      const map: EditMap = { DS: {}, NS: {} };
      for (const row of data) {
        const shift = row.shift as 'DS' | 'NS';
        for (const f of FIELDS) {
          const v = row[f];
          if (v != null) map[shift][f] = String(v);
        }
        if (row.remarks) map[shift]['remarks'] = row.remarks;
      }
      setEdits(map);
    } catch { setEntError('Failed to load existing data.'); }
    finally { setEntLoading(false); }
  }, []);

  useEffect(() => { if (tab === 1) loadEntry(entDate); }, [tab, entDate, loadEntry]);

  const handleChange = (shift: string, field: string, val: string) =>
    setEdits(prev => ({ ...prev, [shift]: { ...prev[shift], [field]: val } }));

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setEntSaving(true); setEntError(''); setEntSuccess('');
    try {
      const entries = ['DS', 'NS'].map(shift => {
        const row = edits[shift] ?? {};
        const base: any = { readingDate: entDate, shift };
        for (const f of FIELDS) {
          const n = num(row[f]);
          if (n !== null) base[f] = n;
        }
        if (row['remarks']) base.remarks = row['remarks'];
        return base;
      }).filter(e => {
        const { readingDate: _rd, shift: _s, ...rest } = e;
        return Object.keys(rest).length > 0;
      });

      if (entries.length === 0) {
        setEntError('Enter at least one value before saving.');
        return;
      }
      await axios.post(`${API}/fostac/batch`, { entries });
      setEntSuccess(`Saved ${entries.length} shift(s) for ${fmtDate(entDate)}.`);
    } catch { setEntError('Save failed. Please try again.'); }
    finally { setEntSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete all FOSTAC data for ${fmtDate(entDate)}?`)) return;
    try {
      await axios.delete(`${API}/fostac/by-date`, { params: { date: entDate } });
      setEdits({ DS: {}, NS: {} });
      setEntSuccess('Data deleted.');
    } catch { setEntError('Delete failed.'); }
  };

  // ── Helpers for display ───────────────────────────────────────────────────
  const cellVal = (row: Record<string,string>, field: FieldDef): string | null => {
    if (field.computed) {
      const v = computeField(field.key, row);
      return v != null ? fmt(v, 4) : null;
    }
    return row[field.key] ?? null;
  };

  const reportCell = (row: any, field: FieldDef) => {
    if (field.computed) return fmt(row[field.key], 4);
    return fmt(row[field.key], 2);
  };

  // ── report grouped by date ─────────────────────────────────────────────
  const reportByDate: Record<string, any[]> = {};
  for (const r of repRows) {
    if (!reportByDate[r.readingDate]) reportByDate[r.readingDate] = [];
    reportByDate[r.readingDate].push(r);
  }

  const totalCols = GROUPS.reduce((s, g) => s + g.fields.length, 0);

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small" variant="outlined">Back</Button>
        <ScienceIcon sx={{ color: '#1b5e20', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1b5e20">FOSTAC and Gas Quality Monitoring</Typography>
          <Typography variant="body2" color="text.secondary">Monthly DS/NS measurements — F1, F2, F3, IST, MP, Gas Quality Fermenters</Typography>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Monthly Report" />
        <Tab label="Data Entry" />
      </Tabs>

      {/* ── MONTHLY REPORT TAB ─── */}
      {tab === 0 && (
        <Box>
          {/* Controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Month</InputLabel>
              <Select value={repMonth} label="Month" onChange={e => setRepMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Year</InputLabel>
              <Select value={repYear} label="Year" onChange={e => setRepYear(Number(e.target.value))}>
                {buildYears().map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <Chip label={`${repRows.length} row(s)`} size="small" color="success" variant="outlined" />
          </Box>

          {repError && <Alert severity="error" sx={{ mb: 2 }}>{repError}</Alert>}
          {repLoading ? <CircularProgress size={28} /> : (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <Table stickyHeader size="small" sx={{ minWidth: 2800 }}>
                <TableHead>
                  {/* Group header row */}
                  <TableRow>
                    <TableCell rowSpan={2} sx={TH()}>Date</TableCell>
                    <TableCell rowSpan={2} sx={TH()}>Shift</TableCell>
                    {GROUPS.map(g => (
                      <TableCell key={g.label} colSpan={g.fields.length}
                        sx={{ ...TH(g.bg), borderLeft: '2px solid rgba(0,0,0,0.15)' }}>
                        {g.label}
                      </TableCell>
                    ))}
                    <TableCell rowSpan={2} sx={TH()}>Remarks</TableCell>
                  </TableRow>
                  {/* Sub-header row */}
                  <TableRow>
                    {GROUPS.flatMap(g => g.fields.map(f => (
                      <TableCell key={`${g.label}-${f.key}`} sx={TH(f.computed ? '#a5d6a7' : g.bg)}>
                        {f.label}
                      </TableCell>
                    )))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(reportByDate).sort().map(date =>
                    reportByDate[date].map((row, ri) => {
                      const rowCount = reportByDate[date].length;
                      return (
                        <TableRow key={`${date}-${row.shift}`} hover
                          sx={{ background: row.shift === 'DS' ? '#fffde7' : '#f3f4f9' }}>
                          {ri === 0 && (
                            <TableCell rowSpan={rowCount}
                              sx={{ ...TD, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {fmtDate(date)}
                            </TableCell>
                          )}
                          <TableCell sx={{ ...TD, fontWeight: 700, color: row.shift === 'DS' ? '#e65100' : '#1a237e' }}>
                            {row.shift}
                          </TableCell>
                          {GROUPS.flatMap(g => g.fields.map(f => {
                            if (g.shared) {
                              if (ri !== 0) return null;
                              return (
                                <TableCell key={f.key} rowSpan={rowCount}
                                  sx={f.computed
                                    ? { ...TD_COMP, verticalAlign: 'middle' }
                                    : { ...TD, verticalAlign: 'middle', background: '#fff' }}>
                                  {reportCell(row, f)}
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell key={f.key} sx={f.computed ? TD_COMP : TD}>
                                {reportCell(row, f)}
                              </TableCell>
                            );
                          }))}
                          <TableCell sx={TD}>{row.remarks ?? ''}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                  {repRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={totalCols + 3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No data for {MONTHS[repMonth - 1]} {repYear}
                      </TableCell>
                    </TableRow>
                  )}
                  {/* ── TOTALS (averages) row ─── */}
                  {repRows.length > 0 && (() => {
                    const dsRows = repRows.filter(r => r.shift === 'DS');
                    const nsRows = repRows.filter(r => r.shift === 'NS');
                    const colAvg = (rows: any[], key: string): number | null => {
                      const vals = rows.map(r => r[key]).filter((v): v is number => v != null && !isNaN(Number(v)));
                      return vals.length > 0 ? vals.reduce((s: number, v: number) => s + Number(v), 0) / vals.length : null;
                    };
                    return (
                      <>
                        {(['DS', 'NS'] as const).map((shift, si) => {
                          const rows = shift === 'DS' ? dsRows : nsRows;
                          return (
                            <TableRow key={`total-${shift}`} sx={{ borderTop: si === 0 ? '2px solid #388e3c' : undefined }}>
                              {si === 0 && (
                                <TableCell rowSpan={2} sx={{ ...TD, fontWeight: 800, background: '#1b5e20', color: '#fff', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                                  AVG
                                </TableCell>
                              )}
                              <TableCell sx={{ ...TD, fontWeight: 800, background: '#e8f5e9', color: shift === 'DS' ? '#e65100' : '#1a237e' }}>
                                {shift}
                              </TableCell>
                              {GROUPS.flatMap(g => g.fields.map(f => {
                                if (g.shared) {
                                  if (si !== 0) return null;
                                  const v = f.computed
                                    ? ratio(colAvg(dsRows, `${f.key.replace('FosTac', '')}Fos`), colAvg(dsRows, `${f.key.replace('FosTac', '')}Tac`))
                                    : colAvg(dsRows, f.key);
                                  return (
                                    <TableCell key={f.key} rowSpan={2}
                                      sx={{ ...(f.computed ? TD_COMP : TD), fontWeight: 700, background: '#f1f8e9', verticalAlign: 'middle' }}>
                                      {fmt(v, f.computed ? 4 : 2)}
                                    </TableCell>
                                  );
                                }
                                const v = f.computed
                                  ? ratio(colAvg(rows, `${f.key.replace('FosTac', '')}Fos`), colAvg(rows, `${f.key.replace('FosTac', '')}Tac`))
                                  : colAvg(rows, f.key);
                                return (
                                  <TableCell key={f.key}
                                    sx={{ ...(f.computed ? TD_COMP : TD), fontWeight: 700, background: '#e8f5e9' }}>
                                    {fmt(v, f.computed ? 4 : 2)}
                                  </TableCell>
                                );
                              }))}
                              <TableCell sx={{ ...TD, background: '#e8f5e9' }} />
                            </TableRow>
                          );
                        })}
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── DATA ENTRY TAB ─── */}
      {tab === 1 && (
        <Box>
          {/* Date picker + actions */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Date" type="date" size="small"
              value={entDate} onChange={e => setEntDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
            />
            <Button variant="contained" startIcon={entSaving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave} disabled={entSaving || entLoading}
              sx={{ bgcolor: '#1b5e20', '&:hover': { bgcolor: '#2e7d32' } }}>
              Save
            </Button>
            <Button variant="outlined" color="error" startIcon={<DeleteSweepIcon />}
              onClick={handleDelete} disabled={entSaving || entLoading}>
              Delete Date
            </Button>
          </Box>

          {entError   && <Alert severity="error"   sx={{ mb: 1 }}>{entError}</Alert>}
          {entSuccess && <Alert severity="success" sx={{ mb: 1 }}>{entSuccess}</Alert>}
          {entLoading && <CircularProgress size={24} sx={{ mb: 1 }} />}

          {/* Card grid — one card per group, no horizontal scrolling */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
            {GROUPS.map(group => (
              <Paper key={group.label} variant="outlined" sx={{ p: 0, overflow: 'hidden', borderRadius: 2, borderTop: `4px solid ${group.bg}` }}>
                {/* Card header */}
                <Box sx={{ px: 2, py: 1, background: group.bg, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                  <Typography variant="subtitle2" fontWeight={700}>{group.label}</Typography>
                </Box>

                <Box sx={{ p: 1.5 }}>
                  {group.shared ? (
                    /* Shared groups — single set of fields, no DS/NS split */
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {group.fields.filter(f => !f.computed).map(f => (
                        <TextField
                          key={f.key}
                          label={f.label}
                          size="small"
                          type="number"
                          value={edits['DS']?.[f.key] ?? ''}
                          onChange={e => handleChange('DS', f.key, e.target.value)}
                          sx={{ width: 110 }}
                        />
                      ))}
                      {/* Computed display */}
                      {group.fields.filter(f => f.computed).map(f => {
                        const v = computeField(f.key, edits['DS'] ?? {});
                        return (
                          <Box key={f.key} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                            <Typography variant="body2" fontWeight={700} color="#2e7d32">
                              {v != null ? fmt(v, 4) : '—'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    /* Non-shared groups — DS and NS stacked */
                    <>
                      {(['DS', 'NS'] as const).map(shift => (
                        <Box key={shift} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                          <Typography variant="caption" fontWeight={800} sx={{
                            color: shift === 'DS' ? '#e65100' : '#1a237e',
                            mb: 0.75, display: 'block',
                            borderBottom: `1px solid ${shift === 'DS' ? '#ffcc80' : '#c5cae9'}`,
                            pb: 0.25,
                          }}>
                            {shift}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {group.fields.filter(f => !f.computed).map(f => (
                              <TextField
                                key={f.key}
                                label={f.label}
                                size="small"
                                type="number"
                                value={edits[shift]?.[f.key] ?? ''}
                                onChange={e => handleChange(shift, f.key, e.target.value)}
                                sx={{ width: 90 }}
                              />
                            ))}
                            {/* Computed FOS/TAC */}
                            {group.fields.filter(f => f.computed).map(f => {
                              const v = computeField(f.key, edits[shift] ?? {});
                              return (
                                <Box key={f.key} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                                  <Typography variant="body2" fontWeight={700} color="#2e7d32">
                                    {v != null ? fmt(v, 4) : '—'}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
              </Paper>
            ))}

            {/* Remarks — full width */}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderTop: '4px solid #9e9e9e', gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Remarks</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {(['DS', 'NS'] as const).map(shift => (
                  <TextField
                    key={shift}
                    label={`${shift} Remarks`}
                    multiline rows={2}
                    size="small"
                    sx={{ flex: 1, minWidth: 240 }}
                    value={edits[shift]?.['remarks'] ?? ''}
                    onChange={e => handleChange(shift, 'remarks', e.target.value)}
                  />
                ))}
              </Box>
            </Paper>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            FOS/TAC is auto-computed from FOS ÷ TAC values.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FostacMonitoring;
